import { AdminFilterValues } from '../Components/Molecules/AdminFilters/AdminFilters'
import { ALL_VALUE, ANY_VALUE_STRING_NUMERIC } from '../../React/Common/Constants/DefaultValues'
import { IsVarEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'

/**
 * <p>True when {@link AdminFilters} has emitted enough scope for lists that need CME plus telephony resolution
 * (or the explicit no-telephony-server path).</p>
 *
 * <p>Matches the partition list gate so debounced {@code onChange} emissions that only have CME (before TelSys
 * options hydrate) do not count as "initial filters ready".</p>
 *
 * @param values - Current admin filter payload from {@link AdminFilters}.
 * @returns Whether CME and telephony scope are in a stable-enough state to drive a first list request.
 */
export const hasResolvedInitialAdminScopeFilters = (values: AdminFilterValues): boolean => {
    if (!isVarNotEmpty(values.cme?.value)) {
        return false
    }

    return values.noTelephonyServer === true || isVarNotEmpty(values.telephonyServer)
}

/**
 * <p>True when TelSys/partition list pages have caught up to debounced {@link AdminFilters} hydration.</p>
 *
 * <p>After CME + TelSys objects exist, a <em>concrete</em> TelSys selection must wait for the partition
 * dropdown to resolve (second debounced tick). {@link ALL_VALUE} TelSys is treated as stable without that
 * wait so the list can clear or aggregate immediately.</p>
 *
 * @param values - Current admin filter payload from {@link AdminFilters}.
 * @returns Whether session/URL bootstrap has applied to TelSys and partition when required.
 */
export const hasResolvedInitialTelSysPartitionListFilters = (values: AdminFilterValues): boolean => {
    if (!hasResolvedInitialAdminScopeFilters(values)) {
        return false
    }

    if (values.noTelephonyServer === true) return true

    const telRaw: unknown = values.telephonyServer?.value

    if (IsVarEmpty(telRaw) || telRaw === ANY_VALUE_STRING_NUMERIC) {
        return false
    }

    if (telRaw === ALL_VALUE) return true

    return isVarNotEmpty(values.partition)
}

/**
 * <p>True when {@link AdminFilters} has emitted a concrete CME scope selection for lists that only need
 * the CME dropdown (no telephony or partition resolution).</p>
 *
 * @param values - Current admin filter payload from {@link AdminFilters}.
 * @returns Whether a CME option value is present so the CM Environment list can load.
 */
export const hasResolvedInitialCmeListFilters = (values: AdminFilterValues): boolean => isVarNotEmpty(values.cme?.value)
